import type { TranslationJob } from '@/entities/localized-speech'
import { useNavigate } from '@tanstack/react-router'
import { BriefcaseBusiness, CheckCircle2, ChevronDown, CircleAlert, Loader2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTranslationJobsStore } from '@/entities/localized-speech'
import { TRANSLATION_JOB_STATUS } from '@/shared/constants/status'
import { Button } from '@/shared/ui/button'

function getJobLanguageProgress(job: TranslationJob) {
  const total = job.outputLanguages.length
  const completedLanguages = new Set(job.completedLanguages ?? Object.keys(job.result ?? {}))
  const completed = job.outputLanguages.filter((language) =>
    completedLanguages.has(language),
  ).length
  return { completed: Math.min(completed, total), total }
}

function formatDurationMs(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// * VI: Đếm thời gian trong component con (mount theo từng job running) để interval luôn chạy, không phụ thuộc store. | EN: Elapsed time lives in a per-running-job child so the interval always runs independently of the store.
function RunningJobElapsed({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const tick = () => {
      setNow(Date.now())
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => {
      window.clearInterval(id)
    }
  }, [])
  const safeStart = Number.isFinite(startedAt) && startedAt > 0 ? startedAt : now
  return <>{formatDurationMs(now - safeStart)}</>
}

// ? VI: Input đọc danh sách job từ global store. | EN: Input reads jobs from the global store.
// ? VI: Output render panel nổi cho job đang chạy/đã xong. | EN: Output renders a floating panel for running/finished jobs.
// * VI: Cung cấp nơi theo dõi/hủy/mở job một cách liên tục. | EN: Provide a persistent place to monitor/cancel/open jobs.
export function BackgroundJobsPanel() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const jobs = useTranslationJobsStore((state) => state.jobs)
  const dismissJob = useTranslationJobsStore((state) => state.dismissJob)
  const markJobForApply = useTranslationJobsStore((state) => state.markJobForApply)
  const requestJobRestore = useTranslationJobsStore((state) => state.requestJobRestore)
  const clearFinishedJobs = useTranslationJobsStore((state) => state.clearFinishedJobs)

  const { runningJobs, finishedJobs } = useMemo(() => {
    // * VI: Tách nhóm running và finished để UI dễ theo dõi. | EN: Split running and finished jobs for clearer UX.
    const running = jobs.filter((job) => job.status === TRANSLATION_JOB_STATUS.PROCESSING)
    const finished = jobs
      .filter((job) => job.status !== TRANSLATION_JOB_STATUS.PROCESSING)
      .slice(0, 8)
    return { runningJobs: running, finishedJobs: finished }
  }, [jobs])

  if (jobs.length < 1) {
    return null
  }

  if (!isOpen) {
    return (
      <Button
        type="button"
        className="fixed bottom-4 right-4 z-120 shadow-xl"
        onClick={() => setIsOpen(true)}
      >
        <BriefcaseBusiness className="h-4 w-4" />
        {t('jobs.panelTitle')}
        {runningJobs.length > 0 && (
          <span className="ml-1 rounded bg-white/20 px-1.5 text-xs tabular-nums">
            {runningJobs.length}
          </span>
        )}
      </Button>
    )
  }

  return (
    <div className="fixed bottom-4 right-4 z-120 w-[min(92vw,28rem)] rounded-xl border border-zinc-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-3">
        <p className="text-sm font-semibold text-zinc-900">{t('jobs.panelTitle')}</p>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={finishedJobs.length < 1}
            onClick={() => {
              if (finishedJobs.length < 1) {
                return
              }
              clearFinishedJobs()
            }}
          >
            {t('jobs.clearFinished')}
          </Button>
          <Button size="icon" variant="ghost" onClick={() => setIsOpen(false)}>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto px-3 py-3">
        <div className="space-y-2">
          {runningJobs.map((job) => {
            const { completed, total } = getJobLanguageProgress(job)
            return (
              <div key={job.id} className="rounded-lg border border-blue-100 bg-blue-50/70 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-900">
                      {job.entityLabel ?? `${job.scope}:${job.entityId}`}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-zinc-600">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t('jobs.running')} <RunningJobElapsed startedAt={job.startedAt} />
                      {total > 0 && (
                        <span className="tabular-nums">
                          · {t('jobs.languagesProgress', { completed, total })}
                        </span>
                      )}
                    </p>
                    {total > 0 && (
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
                        <div
                          className="h-full rounded-full bg-blue-500 transition-all"
                          style={{ width: `${(completed / total) * 100}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {job.entityPath && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const path = job.entityPath
                          if (!path) {
                            return
                          }
                          requestJobRestore(job.id)
                          void navigate({ to: path as never })
                        }}
                      >
                        {t('jobs.open')}
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t('jobs.clearFromPanel')}
                      onClick={() => dismissJob(job.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )
          })}

          {finishedJobs.map((job) => {
            const isSuccess = job.status === TRANSLATION_JOB_STATUS.SUCCESS
            const Icon = isSuccess ? CheckCircle2 : CircleAlert
            const iconClassName = isSuccess ? 'text-emerald-600' : 'text-rose-600'
            const durationMs = job.finishedAt ? job.finishedAt - job.startedAt : 0
            return (
              <div key={job.id} className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-zinc-900">
                      {job.entityLabel ?? `${job.scope}:${job.entityId}`}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-zinc-600">
                      <Icon className={`h-3.5 w-3.5 ${iconClassName}`} />
                      {isSuccess ? t('jobs.success') : t('jobs.failed')}
                      {durationMs > 0 && <> {formatDurationMs(durationMs)}</>}
                    </p>
                    {!isSuccess && job.error && (
                      <p className="mt-1 line-clamp-2 text-xs text-rose-600">{job.error}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {job.entityPath && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const path = job.entityPath
                          if (!path) {
                            return
                          }
                          if (job.status === TRANSLATION_JOB_STATUS.SUCCESS) {
                            markJobForApply(job.id)
                          }
                          void navigate({ to: path as never })
                        }}
                      >
                        {t('jobs.open')}
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => dismissJob(job.id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
